import {
  set as setLocalStorage,
  get as getLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { dateIsBefore } from 'lib/datetime/dateUtils'

import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { DEFAULT_FLEXIBLE_DURATION_RANGE, HOTELS_ENABLED_FILTERS, RECENT_SEARCHES_LIMIT, SEARCH_STORAGE_KEYS } from 'constants/search'

import type { EnabledFilters, RecentDate, RecentFilters, RecentFlexibleSearch } from './type'
import type { IntlShape } from 'react-intl'
import { formatSaleUnitCount } from 'hooks/useSaleUnitPlural'
import moment from 'moment'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { take, unique, uniqueBy } from 'lib/array/arrayUtils'
import config from 'constants/config'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { prettyFlexibleDates } from './SearchForm/SearchDateInput/FlexibleDateSearchField'
import { LOYALTY_SEARCH_FILTER_VALUES } from 'loyalty/constants/loyaltySearchFilters'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import getValueOffPercent from 'lib/offer/getValueOffPercent'

export const searchPaneInteractEventName = 'search-container-pane-interact'

export function formatLocationsDisplayText(intl: IntlShape, searchItem?: App.SearchItem, placeholder?: string) {
  if (!searchItem) {
    return placeholder || intl.formatMessage({
      id: '_m9qMkKnX3ezvNt4Pfee',
      defaultMessage: 'Search for a destination or hotel',
      description: 'Default placeholder text for search location input',
    })
  }

  return searchItem.format.mainText
}

export function getTotalRoomsString(intl: IntlShape, rooms: Array<App.Occupants>, saleUnit?: string) {
  if (saleUnit) {
    const saleUnitText = formatSaleUnitCount(intl, saleUnit, rooms.length)
    return `(${saleUnitText})`
  }
  const roomsText = intl.formatMessage(
    {
      id: 'E0vsEAR7Hn7TaTFIHZbg',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Room count label in search occupancy summary in the search interface',
    },
    { count: rooms.length },
  )
  return `(${roomsText})`
}

export function getChildrenAgesString(intl: IntlShape, rooms: Array<App.Occupants>) {
  const allChildren = rooms.flatMap(room => room.childrenAge)
  const ageLabels = allChildren.map(age =>
    intl.formatMessage(
      {
        id: 'cJEvB8YRY5ZLVBUrv33k',
        defaultMessage: '{age}yo',
        description: 'Child age abbreviation (years old) in search occupancy',
      },
      { age },
    ),
  )
  return ageLabels.length > 0 ? `(${ageLabels.join(', ')})` : ''
}

export function saveRecentRooms(rooms: Array<Array<App.Occupants>>) {
  setLocalStorage(SEARCH_STORAGE_KEYS.TRAVELLERS, rooms)
}

export function loadRecentRooms(): Array<Array<App.Occupants>> {
  return getLocalStorage(SEARCH_STORAGE_KEYS.TRAVELLERS) ?? []
}

export function saveLastDates(dates: RecentDate) {
  setLocalStorage(SEARCH_STORAGE_KEYS.LAST_DATES, dates)
}

export function saveLastFlexibleSelection(flexibleSelection: RecentFlexibleSearch) {
  setLocalStorage(SEARCH_STORAGE_KEYS.FLEXIBLE_OPTIONS, flexibleSelection)
}

export function getLastFlexibleDateSelection() {
  return getLocalStorage(SEARCH_STORAGE_KEYS.FLEXIBLE_OPTIONS)
}

export function clearLastDates() {
  setLocalStorage(SEARCH_STORAGE_KEYS.LAST_DATES, null)
}

export function loadLastDates() {
  const lastDates = getLocalStorage(SEARCH_STORAGE_KEYS.LAST_DATES)
  if (isTodayBeforeCheckin(lastDates?.checkinDate)) return lastDates
}

export function isTodayBeforeCheckin(checkin: string) {
  const today = new Date()
  return !dateIsBefore(new Date(checkin), today)
}

export function isTodayBeforeFlexibleMonth(flexibleMonth: string) {
  const flexibleMonthArray = flexibleMonth.split(',')
  return flexibleMonthArray.some((month) => {
    const momentMonth = moment(month)
    return momentMonth.isAfter()
  })
}

export function isRecentSearchWithinTimeLimit(dateAdded: string, limit: number, unit: moment.unitOfTime.DurationConstructor) {
  const dateAddedMoment = moment(dateAdded)
  const now = moment()
  const diff = now.diff(dateAddedMoment, unit)
  return diff <= limit
}

export function getCheckInCheckOutText(checkIn: string, checkOut: string): string {
  // Parsing the input strings into Moment objects
  const checkInDate = moment(checkIn, 'YYYY-MM-DD')
  const checkOutDate = moment(checkOut, 'YYYY-MM-DD')

  // Extracting components from the Moment objects
  const checkInDay = checkInDate.date()
  const checkInMonth = checkInDate.format('MMM')
  const checkInYear = checkInDate.year()

  const checkOutDay = checkOutDate.date()
  const checkOutMonth = checkOutDate.format('MMM')
  const checkOutYear = checkOutDate.year()

  let output = ''

  // Case 1: Same month, same year
  if (checkInYear === checkOutYear && checkInDate.month() === checkOutDate.month()) {
    output = `${checkInDay} - ${checkOutDay} ${checkInMonth}`
  }
  // Case 2: Different months, but same year
  else if (checkInYear === checkOutYear) {
    output = `${checkInDay} ${checkInMonth} - ${checkOutDay} ${checkOutMonth}`
  }
  // Case 3: Different months, different years
  else {
    output = `${checkInDay} ${checkInMonth} ${checkInYear} - ${checkOutDay} ${checkOutMonth} ${checkOutYear}`
  }

  return output
}

export function getFlexibleDurationAndMonthText(intl: IntlShape, flexibleDuration: DEFAULT_FLEXIBLE_DURATION_RANGE, flexibleMonths: string): string {
  let output = ''

  if (flexibleDuration && flexibleDuration !== DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION) {
    const [minDays, maxDays] = flexibleDuration.split('-')
    output += intl.formatMessage(
      {
        id: 'trQD8GXO3xAAcXpfBa50',
        defaultMessage: '{minDays} - {maxDays} nights',
        description: 'Flexible duration range label showing min and max nights',
      },
      { minDays, maxDays },
    )
  }

  // Handling the flexible months part
  if (flexibleMonths) {
    const months = flexibleMonths.split(',').map(month => moment(month, 'YYYY-MM').format('MMM'))

    if (months.length === 1) {
      output += output ? `, ${months[0]}` : months[0]
    } else if (months.length === 2) {
      output += output ? `, ${months.join(' & ')}` : `${months.join(' & ')}`
    } else {
      output += output ? `, ${months.slice(0, 2).join(' & ')} + ${months.length - 2}` : `${months.slice(0, 2).join(' & ')} + ${months.length - 2}`
    }
  }

  return output
}

export function getRecentSearchDescription(
  intl: IntlShape,
  checkInDate?: string,
  checkOutDate?: string,
  flexibleMonths?: string,
  flexibleDuration?: DEFAULT_FLEXIBLE_DURATION_RANGE,
  isAnytime?: boolean,
): string {
  if (isAnytime) {
    return intl.formatMessage({
      id: '7iPZ9ybbzFRJQGcKgPmd',
      defaultMessage: 'Anytime',
      description: 'Label for flexible date search with no specific dates',
    })
  }

  if (checkInDate && checkOutDate) {
    return getCheckInCheckOutText(checkInDate, checkOutDate)
  }

  if (flexibleMonths || flexibleDuration) {
    return getFlexibleDurationAndMonthText(intl, flexibleDuration ?? DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION, flexibleMonths ?? '')
  }

  return ''
}

const emptyArray: Array<App.FullRecentSearch> = []
export function getRecentSearches(memberId?: string): Array<App.FullRecentSearch> {
  const shouldUseMemberId = !config.ENABLE_LOGGED_OUT_RECENT_SEARCH
  // we currently only store recent searches for a user, this may change but for now return empty if not provided
  if (!memberId && shouldUseMemberId) {
    return emptyArray
  }

  const key = shouldUseMemberId && memberId ? memberId : SEARCH_STORAGE_KEYS.RECENT_SEARCHES

  let recentSearches = getLocalStorage(key) as Array<App.FullRecentSearch> ?? emptyArray

  // if there aren't any recent searches with the *new* key, try to load using the old key
  if (recentSearches.length === 0 && memberId) {
    recentSearches = getLocalStorage(memberId) as Array<App.FullRecentSearch> ?? emptyArray
  }
  return clearSearchDatesIfInPast(recentSearches)
}

export function saveRecentSearch(
  searchItem: App.SearchItem,
  dates: App.SearchDates,
  rooms: Array<App.Occupants>,
  memberId?: string,
) {
  const newSearchItem = {
    searchItem,
    dates,
    rooms,
    recentSearchId: buildRecentSearchId(dates, searchItem, rooms),
    dateAdded: new Date().toISOString(),
  }

  const shouldUseMemberId = !config.ENABLE_LOGGED_OUT_RECENT_SEARCH
  const key = shouldUseMemberId ? memberId : SEARCH_STORAGE_KEYS.RECENT_SEARCHES
  if (!key) {
    // guess they had no member id and we needed, can't save it
    return
  }
  const allSearches = [newSearchItem, ...getRecentSearches(memberId)]
  // Sort V3 items first so uniqueBy keeps them when deduplicating
  const sortedSearches = allSearches.toSorted((a, b) => {
    const aIsV3 = a.searchItem.searchType === 'searchItemV3' ? 0 : 1
    const bIsV3 = b.searchItem.searchType === 'searchItemV3' ? 0 : 1
    return aIsV3 - bIsV3
  })
  const uniqueSearches = uniqueBy(sortedSearches, search => search.searchItem.value)
  const nextSearches = take(uniqueSearches, RECENT_SEARCHES_LIMIT)
  setLocalStorage(key, nextSearches)
}

export function getFormattedDateRange(startDate: moment.Moment, endDate: moment.Moment): string {
  if (startDate.month() === endDate.month()) {
    return `${startDate.format('DD')} - ${endDate.format('DD MMM')}`
  }
  return `${startDate.format('DD MMM')} - ${endDate.format('DD MMM')}`
}

// To remove dates if they are in the past
export function clearSearchDatesIfInPast(searches: Array<App.FullRecentSearch>) {
  return searches.map((search) => {
    if (Object.keys(search.dates).length === 0 || (search.dates.checkIn && !isTodayBeforeCheckin(search.dates.checkIn)) || (search.dates.flexibleMonths && !isTodayBeforeFlexibleMonth(search.dates.flexibleMonths))) {
      const dates: App.SearchDates = { checkIn: undefined, checkOut: undefined, flexibleDuration: undefined, flexibleMonths: undefined, isAnytime: true }
      const recentSearchId = buildRecentSearchId(dates, search.searchItem, search.rooms)
      return { ...search, dates, recentSearchId }
    }
    return search
  })
}

export function deleteRecentSearch(recentSearchId: string, memberId?: string) {
  const searches = getRecentSearches(memberId)
  const shouldUseMemberId = !config.ENABLE_LOGGED_OUT_RECENT_SEARCH
  const key = shouldUseMemberId ? memberId : SEARCH_STORAGE_KEYS.RECENT_SEARCHES
  if (!key) {
    // needs a key to work
    return
  }
  const nextSearches = searches.filter(search => search.recentSearchId !== recentSearchId)
  setLocalStorage(key, nextSearches)
}

// Helper function for getting unique searches
function buildRecentSearchId(dates: App.SearchDates, searchItem: App.SearchItem, rooms: Array<App.Occupants>): string {
  return `${dates?.checkIn}-${dates?.checkOut}-${dates?.flexibleDuration}-${dates?.flexibleMonths}-${searchItem.format.mainText}-${searchItem.searchType}-${formatOccupantsAsGuests(...rooms)}`
}

export function prettyDates(intl: IntlShape, checkinDate?: moment.Moment, checkoutDate?: moment.Moment): string {
  if (checkinDate && checkoutDate) {
    const checkOut = checkoutDate.day() >= 10 ? checkoutDate.format(DMY_CASUAL_FORMAT) : checkoutDate.format('D MMM YYYY')
    let checkinFormat = checkinDate.day() >= 10 ? 'DD' : 'D'
    if (checkinDate.year() === checkoutDate.year()) {
      checkinFormat += checkinDate.month() === checkoutDate.month() ? '' : ' MMM'
    } else {
      checkinFormat += ' MMM YYYY'
    }
    return `${checkinDate.format(checkinFormat)} - ${checkOut}`
  }
  return intl.formatMessage({
    id: 'nEK4H-jsvrG9E8Ppwi5Z',
    defaultMessage: 'Anytime',
    description: 'Label for flexible date search with no specific dates in pretty dates',
  })
}

function getDateIn30Days() {
  const date = new Date()
  date.setDate(date.getDate() + 30)
  return date.getTime()
}

export function getPageTitle(
  intl: IntlShape,
  searchItem: App.SearchItem | undefined,
  dates: {
    checkIn?: moment.Moment
    checkOut?: moment.Moment
    flexibleNights?: FLEXIBLE_DURATION_RANGE
    flexibleMonths?: string
  }): string {
  const { checkIn, checkOut, flexibleNights, flexibleMonths } = dates
  const searchText = searchItem?.format.mainText
  const anytimeLabel = intl.formatMessage({
    id: 'Cde9fVjtwU0urKObGvT-',
    defaultMessage: 'Anytime',
    description: 'Label for anytime date selection in search page title',
  })
  let dateString = anytimeLabel
  const tagline = intl.formatMessage({
    id: 'e3xD4tgTQ2SY3AmEs_J9',
    defaultMessage: 'Luxury Escapes: Handpicked Escapes at the Best Prices',
    description: 'Brand tagline appended to search page title in the search interface',
  })
  if (checkIn && checkOut) {
    dateString = prettyDates(intl, checkIn, checkOut)
  } else if (flexibleNights || flexibleMonths) {
    dateString = prettyFlexibleDates(flexibleMonths, flexibleNights, undefined, intl)
  }
  const hotelsLabel = intl.formatMessage({
    id: 'ZHeAmfu-dy-UAYow0fDM',
    defaultMessage: 'Hotels',
    description: 'Hotels suffix appended to destination name in search page title',
  })
  let resultString = `${searchText}`
  resultString += searchItem?.searchType === 'property' ? '' : ` ${hotelsLabel}`
  resultString += dateString !== anytimeLabel ? `: ${dateString}` : ''
  resultString = intl.formatMessage(
    {
      id: 'xH-bfvN0UYezo5OOQWOA',
      defaultMessage: '{title} · {tagline}',
      description: 'Search page title with brand tagline suffix in the search interface',
    },
    {
      title: resultString,
      tagline,
    },
  )
  return resultString
}

export function getRecentFilters(): RecentFilters {
  const recentFilters = getLocalStorage(SEARCH_STORAGE_KEYS.RECENT_FILTERS)
  if (recentFilters?.expiryDate && Number(recentFilters.expiryDate) > new Date().getTime()) {
    if (recentFilters.customerRatingGte) {
      recentFilters.customerRatingGte = Number(recentFilters.customerRatingGte)
    }
    recentFilters.expiryDate = Number(recentFilters.expiryDate)
    return recentFilters
  }
  return {
    holidayTypes: [],
    amenities: [],
    inclusions: [],
    locations: [],
    expiryDate: getDateIn30Days(),
    orderBy: [],
    bedroomsEq: undefined,
    bedsEq: undefined,
    bathroomsEq: undefined,
    luxPlusFeatures: [],
    agentHubFeatures: [],
  }
}

export function saveRecentFilters(filters: Partial<RecentFilters>) {
  const recentFilters = getRecentFilters()
  for (const _key in filters) {
    const key = _key as keyof RecentFilters
    if (filters[key]) {
      if (key === 'customerRatingGte') {
        recentFilters[key] = filters[key]
        recentFilters.orderBy = unique([filters[key], ...(recentFilters.orderBy ?? [])])
      } else if (key !== 'orderBy' && key !== 'expiryDate' && key !== 'bedroomsEq' && key !== 'bedsEq' && key !== 'bathroomsEq') {
        recentFilters[key] = take(unique(filters[key].concat(...(recentFilters[key] ?? []))), 20)
        recentFilters.orderBy = unique([...filters[key], ...(recentFilters.orderBy ?? [])])
      }
    }
  }
  recentFilters.expiryDate = getDateIn30Days()
  setLocalStorage(SEARCH_STORAGE_KEYS.RECENT_FILTERS, recentFilters)
}

export function getAppliedFilterCount(filters: App.OfferListFilters, enabledFilters: EnabledFilters, excludeOfferTypes = false): number {
  const hasBedroomFilter = !!(filters.bedroomsGte || filters.bedroomsEq)
  const hasBedFilter = !!(filters.bedsGte || filters.bedsEq)
  const hasBathroomFilter = !!(filters.bathroomsGte || filters.bathroomsEq)

  return (enabledFilters.holidayTypes ? filters.holidayTypes?.length ?? 0 : 0) + (filters.amenities?.length ?? 0) +
    (filters.inclusions?.length ?? 0) + (filters?.priceLte ? 1 : 0) + (filters.locations?.length ?? 0) + (filters.customerRatingGte ? 1 : 0) +
    (excludeOfferTypes ? 0 : (filters.offerTypes?.length === 1 ? 1 : 0)) + (hasBedroomFilter ? 1 : 0) + (hasBedFilter ? 1 : 0) + (hasBathroomFilter ? 1 : 0) + (filters.luxPlusFeatures?.length ?? 0)
}

export function getIsEnabledFilterKey(key: string): key is keyof EnabledFilters {
  return key in HOTELS_ENABLED_FILTERS
}

export function formatSearchItemSubtitle(intl: IntlShape, searchItem: App.FullRecentSearch) {
  const { dates, rooms } = searchItem

  const dateText = getRecentSearchDescription(intl, dates.checkIn, dates.checkOut, dates.flexibleMonths, dates.flexibleDuration as DEFAULT_FLEXIBLE_DURATION_RANGE, dates.isAnytime)

  const occupancyText = formatOccupantsAsGuests(...rooms)

  if (dateText === '' || occupancyText === '') return ''

  const roomsText = intl.formatMessage(
    {
      id: 'g7zyI_NLqO29bH-3rIri',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Room count in recent search suggestion subtitle in the search interface',
    },
    { count: rooms.length },
  )
  return intl.formatMessage(
    {
      id: 'AkD77GC8j9e2YN5rX63d',
      defaultMessage: '{dateText} · {occupancyText} · {roomsText}',
      description: 'Recent search suggestion subtitle with dates, occupancy, and room count',
    },
    {
      dateText,
      occupancyText,
      roomsText,
    },
  )
}

export function isOfTypeLuxPlusFeatureFilter(feature: string): boolean {
  const luxPlusFeatureFilters: Array<App.LuxPlusFeatureFilter> = ['Early access', 'Member prices', 'Bonus inclusions', 'Member exclusive']

  return luxPlusFeatureFilters.includes(feature as App.LuxPlusFeatureFilter)
}

const LOYALTY_FEATURE_FILTERS = new Set(LOYALTY_SEARCH_FILTER_VALUES)
export function isOfTypeLoyaltyFeatureFilter(feature: string): boolean {
  return LOYALTY_FEATURE_FILTERS.has(feature)
}

export function getLuxPlusFilterDisplayName(intl: IntlShape, filter: string): string {
  switch (filter) {
    case 'Early access':
      return intl.formatMessage({
        id: '_lQ-OZMhHCBzah2llCGc',
        defaultMessage: 'LuxPlus+ Early access',
        description: 'Display label for the LuxPlus early access filter chip on hotel search',
      })
    case 'Member prices':
      return intl.formatMessage({
        id: 'UE357lkVeUIzoBqeyc5X',
        defaultMessage: 'LuxPlus+ Member prices',
        description: 'Display label for the LuxPlus member prices filter chip on hotel search',
      })
    case 'Bonus inclusions':
      return intl.formatMessage({
        id: 'ZIwZnfAcXKTxAP_sS4Hg',
        defaultMessage: 'LuxPlus+ Bonus inclusions',
        description: 'Display label for the LuxPlus bonus inclusions filter chip on hotel search',
      })
    case 'Member exclusive':
      return intl.formatMessage({
        id: 'h_CSx2LTGguPPVRWrpN8',
        defaultMessage: 'LuxPlus+ Member exclusive',
        description: 'Display label for the LuxPlus member exclusive filter chip on hotel search',
      })
    default:
      return intl.formatMessage(
        {
          id: 'BX4wdEZTcSUVyZjQQx25',
          defaultMessage: 'LuxPlus+ {filterName}',
          description: 'Fallback display label for a LuxPlus hotel search filter chip when the feature name is unknown',
        },
        { filterName: filter },
      )
  }
}

export function getLoyaltyFilterDisplayName(intl: IntlShape, filter: string): string {
  if (filter === 'Free Room Upgrade') {
    return intl.formatMessage({
      id: 'cAuUythHUuZT7-LgxYEi',
      defaultMessage: 'Free upgrade',
      description: 'Display label for Free Room Upgrade loyalty filter',
    })
  }
  return filter
}

export function getAgentHubExclusiveFilterDisplayName(intl: IntlShape, filter: string): string {
  if (filter === 'Agent Hub Exclusive') {
    return intl.formatMessage({
      id: 'D32nYriXyzqNBxZ4wuzj',
      defaultMessage: 'Agent Hub Exclusive',
      description: 'Display label for the Agent Hub exclusive filter chip on hotel search',
    })
  }

  return filter
}

function extractBedbankId(value: string | undefined): string | undefined {
  const bedbankPrefix = 'bedbank:'
  if (value?.startsWith(bedbankPrefix)) {
    return value.substring(bedbankPrefix.length)
  }
  return undefined
}

export function getOfferIdFromSearchItem(searchItem: App.SearchItem): string | undefined {
  // V1 property search item
  if (searchItem.searchType === 'property') {
    return searchItem.offerId ?? extractBedbankId(searchItem.value)
  }

  // V3 search item
  if (searchItem.searchType === 'searchItemV3') {
    if (searchItem.display.offerId) return searchItem.display.offerId

    const { extractedData } = searchItem.searchItem
    if (extractedData.searchType === 'property' && extractedData.property?.propertyType === 'bedbank') {
      return extractedData.property.propertyId
    }
  }

  return undefined
}

export function isFlexibleMonthsWithinDealDates(
  flexibleMonths: string,
  dealStart: moment.Moment,
  dealEnd: moment.Moment,
): boolean {
  return flexibleMonths.split(',').some((month: string) => {
    const startOfMonth = moment(`${month}-01`, 'YYYY-MM-DD')
    const endOfMonth = startOfMonth.clone().endOf('month')

    const currentDay = startOfMonth.clone()
    while (currentDay.isSameOrBefore(endOfMonth, 'day')) {
      if (currentDay.isBetween(dealStart, dealEnd, null, '[]')) {
        return true
      }
      currentDay.add(1, 'day')
    }
    return false
  })
}

export function isDestinationSearchPage(pathname: string) {
  return pathname.includes('location') || pathname.includes('country') || pathname.includes('city') || pathname.includes('location')
}

export function isMetaSearchPage(pathname: string) {
  return pathname.includes('/metasearch')
}

export function isAgentHubExclusiveFilter(filter: string): boolean {
  const agentHubExclusiveFilters: Array<string> = ['Agent Hub Exclusive']
  return agentHubExclusiveFilters.includes(filter)
}

/**
 * Calculates the discount percentage for search menu list item offers
 */
export function getSearchMenuListItemDiscount(searchItem: App.SearchItem, offer?: App.AnyOffer): number | undefined {
  const isPropertyItem =
    (searchItem.searchType === 'property') ||
    (searchItem.searchType === 'searchItemV3' && searchItem.display?.searchType === 'property')

  if (!isPropertyItem || !offer) {
    return undefined
  }

  if (isLEHotel(offer)) {
    const value = offer?.lowestPricePackage?.value ?? 0
    const price = offer?.lowestPricePackage?.price ?? 0
    return getValueOffPercent(value, price)
  }

  if (isBedbankOffer(offer)) {
    return offer?.sell?.discountPercentage
  }

  return undefined
}
